import { Close, Content, Overlay, Portal, Root, Trigger } from '@radix-ui/react-dialog';
import { useLenis } from '@studio-freight/react-lenis';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { AspectRatio } from '@/components/elements/AspectRatio';
import Button from '@/components/elements/Button';
import { CardRenderer } from '@/components/elements/Card';
import StarMuxVideo from '@/components/elements/StarMuxVideo';
import Container from '@/components/global/Container';
import { Icon } from '@/components/global/Icons';
import { Parallax } from '@/components/global/Parallax';
import { IComponentSharedHeroVideo } from '@/graphql/__generated__/global/types';
import { I18nLocales } from '@/interfaces/locales.interfaces';
import { TRACKING_EVENT, track } from '@/lib/gtm';
import { getAllStories } from '@/lib/strapiQueries';
import { formatISO } from 'date-fns';
import { cubicBezier, m as motion } from 'framer-motion';
import { NextSeo, VideoJsonLd } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import BookingWidget from '../BookingSearchBar/BookingWidget';

export const HeroVideo = ({
    title,
    eyebrowText,
    backgroundVideo,
    fullVideo,
    mobileSecondaryCta,
    showBookingBar,
    bookingBarText,
    description,
    pageType,
}: IComponentSharedHeroVideo & {
    title?: string;
    showBookingBar?: boolean;
    bookingBarText?: string;
    description?: string;
    pageType: 'StoryEntity' | 'PageEntity';
}) => {
    const lenis = useLenis();
    const router = useRouter();

    const currentPageVideo = useMemo(() => {
        // This is to track whether to close the dialog or link to new story
        return {
            playbackId: fullVideo?.data?.attributes?.playback_id,
            muxTitle: fullVideo?.data?.attributes?.title,
            title: title,
            description: description,
            slug: '/', // this never gets used
        };
    }, [fullVideo?.data?.attributes?.playback_id, fullVideo?.data?.attributes?.title, title, description]);

    const [currentVideo, setCurrentVideo] = useState(currentPageVideo); // this is used to track the current video or next video up
    const [previousVideo, setPreviousVideo] = useState<{
        // this is to track the link back to the previous story
        playbackId?: string;
        muxTitle?: string;
        title?: string;
        description?: string;
        slug: string;
    }>(currentPageVideo);

    const [isBetweenVideos, setIsBetweenVideos] = useState(false); // trigger state at end of video to show next video card

    const { data: stories } = useQuery(`stories-${router.locale}`, () => getAllStories(router.locale as I18nLocales), { staleTime: Infinity });

    const [resetStoryList, setShouldRender] = useState(false); // a hook to force otherStoryVideos to re-render
    const otherStoryVideos = useMemo(() => {
        return stories
            ?.map((story) => {
                return {
                    playbackId: story?.attributes?.hero?.video?.fullVideo?.data?.attributes?.playback_id,
                    muxTitle: story?.attributes?.hero?.video?.fullVideo?.data?.attributes?.title,
                    title: story?.attributes?.hero?.title,
                    description: story?.attributes?.hero?.description,
                    slug: story.attributes.slug,
                };
            })
            .filter((story) => {
                return story.playbackId && story.playbackId !== currentPageVideo.playbackId;
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stories, currentPageVideo, resetStoryList]);

    // Ideally this would just use the backgroundVideo but just in case lets fallback to the full video
    const bgPlaybackId = backgroundVideo?.data?.attributes?.playback_id;
    const bgMuxTitle = backgroundVideo?.data?.attributes?.title;

    const thumbnail = `https://image.mux.com/${bgPlaybackId}/thumbnail.webp?time=${backgroundVideo?.data?.attributes?.thumbnailTime || 0}`;

    const [count, setCount] = useState(0);
    const playNextVideo = () => {
        setPreviousVideo(currentVideo);
        if (otherStoryVideos && otherStoryVideos.length > 0) {
            const randomIndex = Math.floor(Math.random() * otherStoryVideos.length);
            const randomVideo = otherStoryVideos.splice(randomIndex, 1)[0];
            setCurrentVideo(randomVideo);
        } else {
            setShouldRender(!resetStoryList);
            if (otherStoryVideos && otherStoryVideos.length > 0) {
                playNextVideo();
            }
        }
        setIsBetweenVideos(true);
        setCount(10);

        setTimeout(() => {
            setIsBetweenVideos(false);
        }, 10000); // Wait 10 seconds before auto-playing the next video
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setCount((prevCount) => prevCount - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <section className="relative h-[calc(100vh)] min-h-heroFullScreen flex flex-col">
            <div className="object-cover center inset-1/2 absolute top-0 left-0 w-full h-full overflow-hidden -z-[50]">
                <StarMuxVideo
                    className="min-w-full min-h-full absolute object-cover brightness-50"
                    title={bgMuxTitle || title}
                    playbackId={bgPlaybackId}
                    placeholder={thumbnail}
                    poster={thumbnail}
                    autoPlay
                    playsInline
                    muted
                    loop
                    metadata={{
                        video_title: bgMuxTitle || title,
                    }}
                />
            </div>

            <div className="h-full flex flex-col items-center justify-center text-center text-white m-auto mt-16 lg:mt-24">
                <Container>
                    <Parallax speed={1.5} position="top">
                        <h1 className="flex flex-col">
                            {eyebrowText && <span className="h4 mb-2 lg:mb-4">{eyebrowText}</span>}
                            <span>{title}</span>
                        </h1>
                        {description && (
                            <span>
                                <div className="prose text-white mx-auto mt-8">
                                    <p className="large">{description}</p>
                                </div>
                            </span>
                        )}
                    </Parallax>

                    {fullVideo?.data && (
                        <Root onOpenChange={(open) => (open ? lenis?.stop() : lenis?.start())}>
                            <Parallax speed={1.5} position="top">
                                <Trigger
                                    asChild
                                    onClick={() => {
                                        // Refresh all states back to
                                        setCurrentVideo(currentPageVideo);
                                        setPreviousVideo(currentPageVideo);
                                        setIsBetweenVideos(false);
                                        setShouldRender(!resetStoryList);
                                    }}
                                >
                                    <motion.span
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{
                                            duration: !description ? 0.75 : 1.25,
                                            delay: !description ? 0.75 : 1.25,
                                            ease: cubicBezier(0.35, 0.17, 0.3, 0.86),
                                        }}
                                    >
                                        <Button
                                            variant="fill"
                                            colour="white"
                                            size="lg"
                                            className="mt-8 hover:shadow-xl hover:scale-105"
                                            onClick={() => {
                                                track(TRACKING_EVENT.PRESSED_BUTTON, {
                                                    label: 'Watch Video',
                                                    category: 'Hero Video',
                                                    page: router.asPath,
                                                });
                                            }}
                                        >
                                            <Icon id="play" aria-label="play" className="w-4 h-4 mr-2" />
                                            Watch Video
                                        </Button>
                                    </motion.span>
                                </Trigger>
                            </Parallax>
                            <Portal>
                                <Overlay className="fixed inset-0 bg-black bg-opacity-90 z-50 data-[state=open]:animate-overlayShow" />
                                <Content className="data-[state=open]:animate-contentShow fixed z-50 top-[50%] left-[50%] w-[80vw] translate-x-[-50%] translate-y-[-50%] bg-black shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none rounded-2xl">
                                    <AspectRatio ratio={16 / 9}>
                                        {!isBetweenVideos ? (
                                            <StarMuxVideo
                                                className="w-full rounded-2xl"
                                                title={currentVideo.title || title}
                                                playbackId={currentVideo.playbackId}
                                                poster={`https://image.mux.com/${currentVideo.playbackId}/thumbnail.png`}
                                                controls={true}
                                                autoPlay={true}
                                                playsInline
                                                onEnded={() => {
                                                    pageType === 'StoryEntity' && playNextVideo();
                                                }}
                                                metadata={{
                                                    video_title: currentVideo.title || title,
                                                }}
                                            />
                                        ) : (
                                            <span>
                                                <CardRenderer
                                                    card={{
                                                        title: currentVideo?.title,
                                                        hoverImage: `https://image.mux.com/${currentVideo?.playbackId}/animated.webp?start=10&fps=24`,
                                                        image: `https://image.mux.com/${currentVideo?.playbackId}/animated.webp?start=10&fps=24`,
                                                        description: currentVideo?.description,
                                                        eyebrowText: `Up next in ${count} seconds`,
                                                        aspectRatio: 16 / 9,
                                                    }}
                                                />
                                                <>
                                                    <Button
                                                        onClick={() => {
                                                            setIsBetweenVideos(false);
                                                        }}
                                                        variant="fill"
                                                        colour="white"
                                                        size="lg"
                                                        className="absolute -bottom-16 -right-0 lg:top-4 lg:right-4 lg:bottom-auto text-primary duration-100 ease-in opacity-100"
                                                    >
                                                        <>
                                                            Next Story
                                                            <Icon id="chevron-right" aria-label="cross" className="w-4 h-4 ml-2 fill-primary" />
                                                        </>
                                                    </Button>
                                                    {previousVideo.playbackId !== currentPageVideo.playbackId && (
                                                        <Link href={previousVideo?.slug || '/'} passHref>
                                                            <Button
                                                                variant="fill"
                                                                colour="white"
                                                                size="lg"
                                                                className="absolute -bottom-16 -left-0 lg:top-4 lg:left-4 lg:bottom-auto text-primary duration-100 ease-in opacity-100"
                                                            >
                                                                <Icon id="chevron-left" aria-label="cross" className="w-4 h-4 mr-2 fill-primary" />
                                                                Previous Story
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </>
                                            </span>
                                        )}
                                    </AspectRatio>
                                    <Close asChild>
                                        {!isBetweenVideos ? (
                                            <div className={'fill-white absolute z-[999] -top-4 -right-4'} aria-label="close" role="button">
                                                <Icon id="cross" className="w-4 h-4" />
                                            </div>
                                        ) : (
                                            previousVideo.playbackId === currentPageVideo.playbackId && (
                                                <Button
                                                    variant="ghost"
                                                    colour="white"
                                                    size="lg"
                                                    className="absolute -bottom-16 -left-0 lg:top-4 lg:left-4 lg:bottom-auto duration-100 ease-in opacity-100"
                                                >
                                                    <Icon id="cross" className="w-4 h-4 mr-2 fill-current" />
                                                    Close
                                                </Button>
                                            )
                                        )}
                                    </Close>
                                </Content>
                            </Portal>
                        </Root>
                    )}
                </Container>
            </div>
            {showBookingBar && <BookingWidget secondaryCta={mobileSecondaryCta} bookingBarText={bookingBarText} />}
            {fullVideo?.data && (
                <>
                    <VideoJsonLd
                        keyOverride={fullVideo?.data?.id ? fullVideo?.data?.id : ''}
                        name={fullVideo?.data?.attributes?.title ?? ''}
                        description={fullVideo?.data?.attributes?.title ?? ''}
                        thumbnailurl={thumbnail}
                        thumbnailUrls={thumbnail}
                        contentUrl={`https://stream.mux.com/${fullVideo.data.attributes?.playback_id}/medium.mp4`}
                        uploadDate={fullVideo?.data?.attributes?.createdAt ? formatISO(new Date(fullVideo.data.attributes.createdAt)) : formatISO(new Date())}
                    />
                    <NextSeo
                        twitter={{
                            cardType: 'player',
                        }}
                        openGraph={{
                            title: title,
                            description: description,
                            type: 'video.movie',
                            images: [
                                {
                                    url: thumbnail,
                                    width: 1920,
                                    height: 1080,
                                    alt: title,
                                },
                            ],
                            videos: [
                                {
                                    url: `https://stream.mux.com/${fullVideo.data.attributes?.playback_id}/medium.mp4`,
                                    width: 1920,
                                    height: 1080,
                                    secureUrl: `https://stream.mux.com/${fullVideo.data.attributes?.playback_id}/medium.mp4`,
                                    type: 'video/mp4',
                                },
                            ],
                        }}
                    />
                </>
            )}
        </section>
    );
};

export default HeroVideo;
